import { FormEvent, useState, useEffect, memo, useCallback } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Divider,
  Stack,
  Chip,
  Link,
  Alert,
} from "@mui/material";
import { useAuth } from "../../hooks";
import { AccessTime, LockClock } from "@mui/icons-material";
import { ErrorDisplay } from "../common/ErrorDisplay";

/**
 * Enhanced MFA verification form with performance optimizations
 *
 * Features:
 * - Memoized to prevent unnecessary re-renders
 * - Optimized event handlers
 * - Improved UI for code entry
 * - Visual countdown timer
 * - Standardized error handling
 */

interface MfaVerificationFormProps {
  initialEmail?: string;
}

const MFA_SESSION_TIMEOUT = 5 * 60 * 1000; // 5 minutes in milliseconds

const MfaVerificationForm: React.FC<MfaVerificationFormProps> = ({
  initialEmail = "",
}) => {
  const {
    verifyMfa,
    resendMfaCode,
    error: authError,
    isLoading,
    clearError,
  } = useAuth();

  const [email] = useState<string>(initialEmail);
  const [mfaCode, setMfaCode] = useState<string>("");
  const [validationError, setValidationError] = useState<string>("");
  const [remainingTime, setRemainingTime] =
    useState<number>(MFA_SESSION_TIMEOUT);
  const [isExpired, setIsExpired] = useState<boolean>(false);
  const [isResending, setIsResending] = useState<boolean>(false);

  // Format remaining time as MM:SS
  const formattedRemainingTime = useCallback(() => {
    const minutes = Math.floor(remainingTime / 60000);
    const seconds = Math.floor((remainingTime % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  }, [remainingTime]);

  // Countdown timer for MFA session
  useEffect(() => {
    if (remainingTime <= 0) {
      setIsExpired(true);
      return;
    }

    const timerId = setInterval(() => {
      setRemainingTime((prev) => prev - 1000);
    }, 1000);

    return () => clearInterval(timerId);
  }, [remainingTime]);

  // Validate MFA code
  const validateMfaCode = useCallback(
    (code: string): boolean => {
      if (isExpired) {
        setValidationError(
          "MFA session has expired. Please request a new code.",
        );
        return false;
      }

      if (!code || code.length !== 6) {
        setValidationError("MFA code must be 6 digits");
        return false;
      }

      setValidationError("");
      return true;
    },
    [isExpired],
  );

  // Handle MFA verification
  const handleVerify = useCallback(async () => {
    setValidationError("");
    if (authError) clearError();

    if (!validateMfaCode(mfaCode)) return;

    try {
      await verifyMfa({ email, code: mfaCode });
      // Don't navigate here - let the parent component handle navigation
      // based on the full authentication state including forcePasswordChange
    } catch (error) {
      // Error is handled in AuthProvider
    }
  }, [validateMfaCode, mfaCode, email, verifyMfa, authError, clearError]);

  // Handle resend of MFA code
  const handleResendCode = useCallback(async () => {
    setValidationError("");
    clearError();
    setIsResending(true);

    try {
      await resendMfaCode(email);
      setRemainingTime(MFA_SESSION_TIMEOUT);
      setIsExpired(false);
      setMfaCode("");
    } catch (error) {
      setValidationError("Failed to resend code. Please try again.");
    } finally {
      setIsResending(false);
    }
  }, [email, resendMfaCode, clearError]);

  // Memoized form submission handler
  const handleSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      handleVerify();
    },
    [handleVerify],
  );

  // Handle MFA code changes
  const handleMfaCodeChange = useCallback(
    (code: string) => {
      const sanitizedCode = code.replace(/[^0-9]/g, "").slice(0, 6);
      setMfaCode(sanitizedCode);
      if (validationError) setValidationError("");
      if (authError) clearError();
    },
    [validationError, authError, clearError],
  );

  // Memoized code change handler
  const onCodeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleMfaCodeChange(e.target.value);
    },
    [handleMfaCodeChange],
  );

  // Automatically submit when code is fully entered
  useEffect(() => {
    if (mfaCode.length === 6 && !isLoading && !isExpired && !authError) {
      handleVerify();
    }
  }, [mfaCode, isLoading, isExpired, authError, handleVerify]);

  // Memoized back button handler using direct navigation
  const handleGoBack = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    // Directly navigate to login instead of using history.back()
    // This avoids potential loop issues
    window.location.href = "/auth/login";
  }, []);

  // Ensure we have an active MFA verification session
  useEffect(() => {
    if (initialEmail && !isLoading) {
      console.log("MFA verification active for email:", initialEmail);
    }
  }, [initialEmail, isLoading]);

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: "100%" }}>
      <Typography component="h2" variant="h6" align="center" gutterBottom>
        Two-Factor Authentication
      </Typography>

      <Typography variant="body2" align="center" sx={{ mb: 1 }}>
        Enter the 6-digit code sent to your device
      </Typography>

      {/* Display the countdown timer */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Chip
          icon={
            isExpired ? (
              <LockClock fontSize="small" />
            ) : (
              <AccessTime fontSize="small" />
            )
          }
          label={
            isExpired
              ? "Code has expired"
              : `Code expires in: ${formattedRemainingTime()}`
          }
          color={isExpired ? "error" : "default"}
          variant="outlined"
        />
      </Box>

      {/* Display errors */}
      {authError && <ErrorDisplay error={authError} onClose={clearError} />}

      {isExpired && !validationError && !authError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Your verification code has expired. Please request a new one.
        </Alert>
      )}

      {/* MFA code input - styled for code entry */}
      <TextField
        margin="normal"
        required
        fullWidth
        id="mfaCode"
        label="6-digit code"
        name="mfaCode"
        autoFocus
        value={mfaCode}
        onChange={onCodeChange}
        error={!!validationError}
        helperText={validationError}
        disabled={isLoading || isExpired}
        inputProps={{
          maxLength: 6,
          inputMode: "numeric",
          pattern: "[0-9]*",
          style: {
            letterSpacing: "0.5em",
            textAlign: "center",
            fontSize: "1.5rem",
          },
        }}
        sx={{
          "& .MuiInputBase-input": {
            textAlign: "center",
          },
        }}
      />

      <Stack direction="column" spacing={2} sx={{ mt: 3 }}>
        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={isLoading || mfaCode.length !== 6 || isExpired}
        >
          {isLoading ? <CircularProgress size={24} /> : "Verify"}
        </Button>

        <Divider>
          <Chip label="OR" size="small" />
        </Divider>

        <Button
          fullWidth
          variant="outlined"
          disabled={isResending}
          onClick={handleResendCode}
          startIcon={isResending ? <CircularProgress size={16} /> : null}
        >
          {isResending ? "Sending..." : "Resend Code"}
        </Button>
      </Stack>

      <Typography
        variant="body2"
        align="center"
        color="textSecondary"
        sx={{ mt: 3 }}
      >
        Having trouble?{" "}
        <Link href="#" onClick={handleGoBack}>
          Go back
        </Link>{" "}
        or contact support
      </Typography>
    </Box>
  );
};

// Memoize the component to prevent unnecessary re-renders
export default memo(MfaVerificationForm);
